// @flow
import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import DateRangeIcon from 'material-ui/svg-icons/action/date-range';
import { translate } from 'react-i18next';

import Logo from 'common/components/Logo';
import SplashCard from 'common/components/SplashCard';
import CumulativeCampaignMetricByDayChart from 'common/modules/campaign/components/CumulativeCampaignMetricByDayLineChart';
import OverviewCard from 'common/components/OverviewCard';
import GenderGraph from 'common/components/charts/GenderGraph';
import { Currency, Numeric } from 'common/components/Number';
import DateRangePicker from 'common/components/DateRangePicker';
import Campaign from 'common/modules/campaign/domain/Campaign';
import CampaignMetrics from 'common/modules/campaign/domain/CampaignMetrics';
import Page from 'common/components/Page';
import Card from 'common/components/Card';
import AgeRangeGraph from 'common/components/charts/AgeRangeGraph';
import ProgressBar from 'common/components/ProgressBar';

import CampaignMessageComponent from './components/CampaignMessageComponent';
import LoadingSpinner from '../../../common/components/LoadingSpinner';

const SMALL_VIEW_WIDTH = 650;

const Row = styled.div`
  display: flex;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MetricTitle = styled.div`
  font-size: 16px;
`;

const GroupedMetricsCard = styled(Card)`
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  padding: ${({ theme }) => theme.metrics.margin}px 0;
`;

const IndividualMetric = styled.div`
  text-align: center;
`;

const MetricsCard = styled(Card)`
  height: 600px;
  padding: ${({ theme }) => theme.metrics.margin}px;
  margin-bottom: ${({ theme }) => theme.metrics.margin}px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DateRangeWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap-reverse;
`;

const GraphWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const InfoWrapper = styled.div`
  width: 100%;
`;

const AdvertiserName = styled.span`
  display: block;
  font-weight: bold;
`;

const InfoTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.metrics.smallMargin}px;
  text-transform: capitalize;
`;

const InfoText = styled.p`
  margin-top: 0;
  text-align: justify;
  max-width: 1000px;
`;

const CenterWrapper = styled.div`
  justify-content: center;
  text-align: center;
  margin-top: 25vh;
  margin-left: auto;
  margin-right: auto;
`;

const Subtitle = styled.div`
  flex-grow: 1;
  font-size: 21px;
  text-align: right;
  color: ${({ theme }) => theme.colors.primaryText};
  margin-bottom: 10px;

  @media screen and (max-width: ${SMALL_VIEW_WIDTH}px) {
    text-align: center;
  }
`;

const RightLogo = styled(Logo)`
  display: block;
  margin-left: auto;

  @media screen and (max-width: ${SMALL_VIEW_WIDTH}px) {
    display: none;
  }
`;

const GraphTitle = styled.div`
  font-size: ${({ theme }) => theme.fonts.style.sub.fontSize}px;
  color: ${({ theme }) => theme.colors.primaryText};
  margin-bottom: ${({ theme }) => theme.metrics.smallMargin}px;
`;

type CampaignNotStartedConfig = {
  image: any;
  buttonCallback: () => void;
  buttonText: string;
};

type Props = {
  campaign: Campaign;
  metrics?: CampaignMetrics;
  campaignNotStarted: CampaignNotStartedConfig;

  isFetchingMetrics?: boolean;
  hasMetricsError: boolean;
  showRadio: ?boolean;

  removeAudioFileMessage: (string) => Promise<Campaign>;
  uploadAudioFileMessage: (string, File) => Promise<Campaign>;
  editMessage: (string, string) => Promise<Campaign>;

  theme: Object;
  t: any;
};

function CampaignOverview(props: Props) {
  const {
    campaign,
    campaignNotStarted: {
      image,
      buttonText,
      buttonCallback,
    },
    isFetchingMetrics,
    hasMetricsError,
    showRadio,
    theme,
    t,
  } = props;

  const metricStyle = { fontSize: theme.fonts.style.secondary.fontSize, color: theme.colors.primary };

  const metricsDateRange = campaign.getFullDateRange(props.metrics);

  const { from, to } = metricsDateRange;
  const metrics = props.metrics && props.metrics.padMissingMetricsUntil(metricsDateRange.boundedToYesterday());


  let status;
  if (campaign.isBooked()) {
    status = t('startsIn', { time: from.fromNow(true) });
  } else if (campaign.isCompleted()) {
    status = t('completed');
  } else if (campaign.shouldHaveCompleted()) {
    status = t('shouldHaveCompleted', { time: campaign.getShouldHaveCompletedTime() });
  } else {
    status = t('endsIn', { time: to.fromNow(true) });
  }

  return (
    <Page fluid title={campaign.name}>
      <RightLogo src={showRadio && campaign.radio.logo} />
      <HeaderWrapper>
        <DateRangeWrapper>
          <DateRangeIcon />
          <DateRangePicker from={from} to={to} minDate={from} maxDate={to} disabled />
        </DateRangeWrapper>
        <Subtitle>
          <AdvertiserName>{campaign.advertiser.name}</AdvertiserName>
          <span>{status}</span>
        </Subtitle>
      </HeaderWrapper>

      {campaign.isBooked() &&
      <SplashCard
        image={image}
        title={t('campaignWillBeginTitle', { days: from.fromNow(true) })}
        text={t('campaignWillBeginText')}
        buttonText={buttonText}
        onButtonClick={buttonCallback}
      />
      }
      {!isFetchingMetrics && hasMetricsError && !campaign.isBooked() &&
      <SplashCard
        image={image}
        title={t('fetchingMetricsErrorTitle')}
        text={t('fetchingMetricsErrorText')}
        buttonText={buttonText}
        onButtonClick={buttonCallback}
      />
      }
      {isFetchingMetrics &&
      <CenterWrapper>
        <LoadingSpinner size="medium" />
      </CenterWrapper>
      }
      {(campaign.isRunning() || campaign.isCompleted()) && metrics && !isFetchingMetrics &&
      <div>
        <GraphWrapper>
          <div className="row" style={{ width: '100%' }}>
            <div className="col-lg-12 col-xs-12" style={{ marginBottom: theme.metrics.margin }}>
              <GroupedMetricsCard>
                <IndividualMetric>
                  <MetricTitle>{t('impressions')}</MetricTitle>
                  <Numeric value={metrics.impressions} roundAt={0} style={metricStyle} />
                </IndividualMetric>
                <IndividualMetric>
                  <MetricTitle>{t('totalCost')}</MetricTitle>
                  <Currency value={campaign.totalCost} roundAt={1} style={metricStyle} />
                </IndividualMetric>
                <IndividualMetric>
                  <MetricTitle>{t('campaigns:spotsSold', { count: campaign.numberOfSpotsSold })}</MetricTitle>
                  <Numeric value={campaign.numberOfSpotsSold} roundAt={1} style={metricStyle} />
                </IndividualMetric>
              </GroupedMetricsCard>
            </div>

            <div className="col-lg-8 col-xs-12">
              <MetricsCard>
                <GraphTitle>{t('impressions')}</GraphTitle>
                <CumulativeCampaignMetricByDayChart
                  primary={theme.colors.primary}
                  secondary={theme.colors.darkPrimary}
                  metricByDate={metrics.cumulativeImpressionsByDate}
                  spotsByDate={metrics.spotsByDate}
                  from={from}
                  to={to}
                  proposition={campaign.proposition}
                  withTooltip
                  withAnimation
                />
              </MetricsCard>
            </div>
            <Column className="col-lg-4 col-xs-12">
              <Row style={{ marginBottom: theme.metrics.margin }}>
                <OverviewCard small title={t('campaignProgression')} titlePosition="center">
                  <div style={{ width: '80%' }}>
                    <ProgressBar value={metrics.progress} maxValue={1} />
                  </div>
                </OverviewCard>
              </Row>
              <Row>
                <OverviewCard
                  small
                  title={t('demography')}
                  titlePosition="center"
                  containerStyle={{ maxWidth: '100%' }}
                >
                  <GenderGraph
                    data={metrics.genderDimension}
                    legendPosition="bottom"
                  />
                </OverviewCard>
              </Row>
              <Row style={{ marginTop: theme.metrics.margin }}>
                <OverviewCard small title={t('ageRange')}>
                  <AgeRangeGraph data={metrics.ageGroup} height={148} hideCommercialAgeGroup />
                </OverviewCard>
              </Row>
            </Column>
            <InfoWrapper>
              <InfoTitle>{t('description')}</InfoTitle>
              <InfoText>{campaign.description}</InfoText>
            </InfoWrapper>
          </div>
        </GraphWrapper>
      </div>
      }

      {props.campaign &&
      <CampaignMessageComponent
        uploadAudioFileMessage={props.uploadAudioFileMessage}
        editMessage={props.editMessage}
        removeAudioFileMessage={props.removeAudioFileMessage}
        message={props.campaign.message}
        audioFilePath={props.campaign.audioFilePath}
      />
      }
    </Page>
  );
}

export default withTheme(translate('commonCampaign', 'campaigns')(CampaignOverview));



// WEBPACK FOOTER //
// ./src/statsradio/modules/campaigns/CampaignOverview.js